import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { computed, Inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { DEFAULT_LANGUAGE, SupportedLanguages, TRANSLATION_FILES } from '../../shared/constants/translation-config';

export interface TranslationData {
  [key: string]: any;
}

@Injectable({
  providedIn: 'root'
})
export class TranslateService {
  private currentLanguage = signal<string>(DEFAULT_LANGUAGE);
  private translations = signal<{ [key: string]: TranslationData }>({});
  private loadingTranslations = signal<boolean>(false);

  public currentLang = computed(() => this.currentLanguage());
  public isLoading = computed(() => this.loadingTranslations());

  constructor(
    private http: HttpClient,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.initializeLanguage();
  }

  private initializeLanguage(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    // Obtener idioma del localStorage o usar español por defecto
    const savedLang = localStorage.getItem('language') || DEFAULT_LANGUAGE; 
    this.setLanguage(this.validateLanguage(savedLang));
  }

  setLanguage(lang: string): void {
    this.currentLanguage.set(lang);

    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem('language', lang);
    }
    this.loadTranslations(lang);
  }

  private loadTranslations(lang: string): void {
    if (lang.includes('-')) lang = lang.split('-')[0]?.toLowerCase();
    this.loadingTranslations.set(true);
    
    const translationFiles = TRANSLATION_FILES;
    const translations: { [key: string]: TranslationData } = {};
    
    // Cargar archivos uno por uno para mejor manejo de errores
    let loadedCount = 0;
    
    translationFiles.forEach(file => {
      this.loadTranslationFile(lang, file).subscribe({
        next: (data) => {
          translations[file] = data;
          loadedCount++;
          
          if (loadedCount === translationFiles.length) {
            this.translations.set(translations);
            this.loadingTranslations.set(false);
          }
        },
        error: (error) => {
          console.error(`Error loading ${file}:`, error);
          loadedCount++;
          
          if (loadedCount === translationFiles.length) {
            this.translations.set(translations);
            this.loadingTranslations.set(false);
          }
        }
      });
    });
  }

  private loadTranslationFile(lang: string, file: string): Observable<TranslationData> {
    const path = `assets/i18n/${lang}/${file}.json`;
    
    return this.http.get<TranslationData>(path).pipe(
      catchError(error => {
        console.error(`Error loading translation file ${path}:`, error);
        console.error('Full error:', error);
        
        if (lang === 'en') {
          return this.http.get<TranslationData>(`assets/i18n/es/${file}.json`).pipe(
            catchError(spanishError => {
              console.error(`Error loading Spanish fallback for ${file}:`, spanishError);
              return of({});
            })
          );
        }
        return of({});
      })
    );
  }

  translate(key: string, params?: { [key: string]: string | number }): string {
    if (this.loadingTranslations()) {
      return key;
    }

    const translations = this.translations();
    const [module, ...keyParts] = key.split('.');
    if (!translations[module]) {
      if (!this.loadingTranslations()) {
        console.warn(`Translation module '${module}' not found`);
      }
      return key;
    }

    let value: any = translations[module];
    
    for (const part of keyParts) {
      if (value && typeof value === 'object' && value[part]) {
        value = value[part];
      } else {
        if (!this.loadingTranslations()) {
          console.warn(`Translation key '${key}' not found`);
        }
        return key;
      }
    }

    if (typeof value !== 'string') {
      if (!this.loadingTranslations()) {
        console.warn(`Translation value for '${key}' is not a string`);
      }
      return key;
    }

    // Replace parameters if provided
    if (params) {
      Object.keys(params).forEach(param => {
        value = value.replace(`{{${param}}}`, String(params[param]));
      });
    }

    return value;
  }

  // Check if translations are loaded
  areTranslationsLoaded(): boolean {
    return Object.keys(this.translations()).length > 0;
  }

  // Check if translations are currently loading
  isLoadingTranslations(): boolean {
    return this.loadingTranslations();
  }

  // Initialize translations (for APP_INITIALIZER)
  async initializeTranslations(): Promise<void> {
    await this.loadTranslations(this.currentLanguage());
  }

   validateLanguage(language: string): string {
    return language.toLocaleLowerCase().includes(SupportedLanguages.ES) ? SupportedLanguages.ES : SupportedLanguages.EN;
  }
}
